import React, { useState } from 'react';

const s = {
  container: { display: 'flex', flexDirection: 'column', gap: '14px' },
  sectionLabel: {
    fontFamily: '"Geist Mono", monospace', fontSize: '10px',
    letterSpacing: '0.08em', textTransform: 'uppercase',
    color: 'var(--muted-foreground)', marginBottom: '6px',
  },
  row: { display: 'flex', alignItems: 'center', gap: '10px' },
  paramName: {
    fontFamily: '"Geist Mono", monospace', fontSize: '12px',
    color: 'var(--foreground)', minWidth: '120px', flexShrink: 0,
  },
  required: { color: 'var(--destructive)', marginLeft: '2px', fontSize: '10px' },
  input: {
    fontFamily: '"Geist Mono", monospace', fontSize: '12px', flex: 1,
    background: 'var(--card)', border: '1px solid var(--input)',
    borderRadius: 'var(--radius)', color: 'var(--foreground)',
    padding: '6px 10px', outline: 'none',
    transition: 'border-color 0.15s',
  },
  select: {
    fontFamily: '"Geist Mono", monospace', fontSize: '12px',
    background: 'var(--card)', border: '1px solid var(--input)',
    borderRadius: 'var(--radius)', color: 'var(--foreground)',
    padding: '6px 10px', outline: 'none', flex: 1,
  },
  textarea: {
    fontFamily: '"Geist Mono", monospace', fontSize: '12px', width: '100%',
    background: 'var(--card)', border: '1px solid var(--input)',
    borderRadius: 'var(--radius)', color: 'var(--foreground)',
    padding: '8px 10px', outline: 'none', resize: 'vertical', minHeight: '100px',
  },
  sendBtn: {
    fontFamily: 'Geist, sans-serif', fontWeight: 500, fontSize: '13px',
    padding: '8px 20px', borderRadius: 'var(--radius)',
    border: '1px solid var(--border)',
    background: 'var(--primary)', color: 'var(--primary-foreground)',
    cursor: 'pointer', alignSelf: 'flex-start',
    transition: 'opacity 0.15s',
  },
  corsWarn: {
    background: 'oklch(from var(--method-put) l c h / 0.08)',
    border: '1px solid oklch(from var(--method-put) l c h / 0.25)',
    borderRadius: 'var(--radius)', padding: '12px 14px',
    fontSize: '12px', color: 'var(--method-put)', lineHeight: 1.65,
  },
  errBox: {
    background: 'oklch(from var(--destructive) l c h / 0.08)',
    border: '1px solid oklch(from var(--destructive) l c h / 0.25)',
    borderRadius: 'var(--radius)', padding: '12px 14px',
    fontSize: '12px', color: 'var(--destructive)', lineHeight: 1.65,
  },
  responseBox: {
    borderRadius: 'var(--radius)', border: '1px solid var(--border)',
    overflow: 'hidden',
  },
  responseHeader: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '9px 14px', background: 'var(--muted)',
    borderBottom: '1px solid var(--border)',
  },
  statusBadge: {
    fontFamily: '"Geist Mono", monospace', fontSize: '12px', fontWeight: 600,
  },
  toggleHeadersBtn: {
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    background: 'none', border: 'none',
    color: 'var(--muted-foreground)', cursor: 'pointer',
    textDecoration: 'underline',
  },
  elapsed: {
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    color: 'var(--muted-foreground)', marginLeft: 'auto',
  },
  responseBody: {
    padding: '14px', background: 'var(--card)',
    fontFamily: '"Geist Mono", monospace', fontSize: '12px',
    color: 'var(--card-foreground)', whiteSpace: 'pre-wrap',
    wordBreak: 'break-all', maxHeight: '300px', overflowY: 'auto',
    lineHeight: 1.65,
  },
};

function statusColor(code) {
  if (!code) return 'var(--muted-foreground)';
  if (code >= 200 && code < 300) return 'var(--method-get)';
  if (code >= 300 && code < 400) return 'var(--method-post)';
  if (code >= 400 && code < 500) return 'var(--method-put)';
  return 'var(--method-delete)';
}

export default function TryItPanel({ path, method, operation, servers }) {
  const params = operation.parameters || [];
  const [paramValues, setParamValues] = useState({});
  const [body, setBody] = useState('');
  const [serverUrl, setServerUrl] = useState(servers[0]?.url || '');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showHeaders, setShowHeaders] = useState(false);

  const pathParams   = params.filter((p) => p.in === 'path');
  const queryParams  = params.filter((p) => p.in === 'query');
  const headerParams = params.filter((p) => p.in === 'header');
  const hasBody = ['post', 'put', 'patch'].includes(method.toLowerCase()) || !!operation.requestBody;

  const set = (name, val) => setParamValues((v) => ({ ...v, [name]: val }));

  const buildUrl = () => {
    let url = serverUrl.replace(/\/$/, '') + path;
    for (const [k, v] of Object.entries(paramValues)) url = url.replace(`{${k}}`, encodeURIComponent(v));
    const qp = queryParams
      .filter((p) => paramValues[p.name])
      .map((p) => `${encodeURIComponent(p.name)}=${encodeURIComponent(paramValues[p.name])}`);
    if (qp.length) url += '?' + qp.join('&');
    return url;
  };

  const handleSend = async () => {
    setLoading(true); setError(null); setResponse(null);
    const t0 = Date.now();
    try {
      const hdrs = { 'Content-Type': 'application/json' };
      for (const p of headerParams) if (paramValues[p.name]) hdrs[p.name] = paramValues[p.name];
      const opts = { method: method.toUpperCase(), headers: hdrs };
      if (hasBody && body.trim()) opts.body = body;
      const res = await fetch(buildUrl(), opts);
      const elapsed = Date.now() - t0;
      const responseHeaders = {};
      res.headers.forEach((val, key) => { responseHeaders[key] = val; });
      const ct = res.headers.get('content-type') || '';
      let responseBody = ct.includes('json')
        ? JSON.stringify(await res.json(), null, 2)
        : await res.text();
      setResponse({ status: res.status, statusText: res.statusText, headers: responseHeaders, body: responseBody, elapsed });
    } catch (e) {
      const isCors = e.name === 'TypeError';
      setError({ cors: isCors, message: e.message });
    } finally {
      setLoading(false);
    }
  };

  const ParamField = ({ p }) => {
    const schema = p.schema || {};
    if (schema.enum) {
      return (
        <select style={s.select} value={paramValues[p.name] || ''} onChange={(e) => set(p.name, e.target.value)}>
          <option value="">— select —</option>
          {schema.enum.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      );
    }
    return (
      <input
        style={s.input} placeholder={p.description || p.name}
        value={paramValues[p.name] || ''} onChange={(e) => set(p.name, e.target.value)}
      />
    );
  };

  return (
    <div style={s.container}>
      {/* Server */}
      {servers.length > 0 && (
        <div>
          <div style={s.sectionLabel}>Server</div>
          <select style={{ ...s.select, flex: 'none', width: '100%' }} value={serverUrl} onChange={(e) => setServerUrl(e.target.value)}>
            {servers.map((sv, i) => (
              <option key={i} value={sv.url}>{sv.url || 'No URL'}{sv.description ? ` — ${sv.description}` : ''}</option>
            ))}
          </select>
        </div>
      )}

      {/* Path params */}
      {pathParams.length > 0 && (
        <div>
          <div style={s.sectionLabel}>Path Parameters</div>
          {pathParams.map((p) => (
            <div key={p.name} style={{ ...s.row, marginBottom: '6px' }}>
              <span style={s.paramName}>{p.name}{p.required && <span style={s.required}>*</span>}</span>
              <ParamField p={p} />
            </div>
          ))}
        </div>
      )}

      {/* Query params */}
      {queryParams.length > 0 && (
        <div>
          <div style={s.sectionLabel}>Query Parameters</div>
          {queryParams.map((p) => (
            <div key={p.name} style={{ ...s.row, marginBottom: '6px' }}>
              <span style={s.paramName}>{p.name}{p.required && <span style={s.required}>*</span>}</span>
              <ParamField p={p} />
            </div>
          ))}
        </div>
      )}

      {/* Header params */}
      {headerParams.length > 0 && (
        <div>
          <div style={s.sectionLabel}>Headers</div>
          {headerParams.map((p) => (
            <div key={p.name} style={{ ...s.row, marginBottom: '6px' }}>
              <span style={s.paramName}>{p.name}{p.required && <span style={s.required}>*</span>}</span>
              <input style={s.input} placeholder={p.description || p.name}
                value={paramValues[p.name] || ''} onChange={(e) => set(p.name, e.target.value)} />
            </div>
          ))}
        </div>
      )}

      {/* Body */}
      {hasBody && (
        <div>
          <div style={s.sectionLabel}>Request Body (JSON)</div>
          <textarea style={s.textarea} placeholder='{"key": "value"}' value={body} onChange={(e) => setBody(e.target.value)} />
        </div>
      )}

      {/* Send */}
      <button
        style={{ ...s.sendBtn, opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
        onClick={handleSend} disabled={loading}
      >
        {loading ? 'Sending…' : `Send ${method.toUpperCase()}`}
      </button>

      {/* CORS warning */}
      {error?.cors && (
        <div style={s.corsWarn}>
          <strong>CORS Error</strong> — The server does not allow browser requests directly. This is a server-side restriction, not an APIScribe issue. Try a CORS proxy or test from a backend environment.
          <div style={{ marginTop: '8px', fontSize: '11px', opacity: 0.7 }}>{error.message}</div>
        </div>
      )}
      {error && !error.cors && (
        <div style={s.errBox}><strong>Request failed:</strong> {error.message}</div>
      )}

      {/* Response */}
      {response && (
        <div style={s.responseBox}>
          <div style={s.responseHeader}>
            <span style={{ ...s.statusBadge, color: statusColor(response.status) }}>
              {response.status} {response.statusText}
            </span>
            <button style={s.toggleHeadersBtn} onClick={() => setShowHeaders((h) => !h)}>
              {showHeaders ? 'Hide headers' : 'Show headers'}
            </button>
            <span style={s.elapsed}>{response.elapsed}ms</span>
          </div>
          {showHeaders && (
            <div style={{ ...s.responseBody, maxHeight: '130px', borderBottom: '1px solid var(--border)' }}>
              {Object.entries(response.headers).map(([k, v]) => (
                <div key={k}><span style={{ color: 'var(--foreground)' }}>{k}</span>: {v}</div>
              ))}
            </div>
          )}
          <div style={s.responseBody}>{response.body || '(empty response body)'}</div>
        </div>
      )}
    </div>
  );
}
